// Version: 1.2.8
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, Loader2, AlertCircle, CheckCircle } from 'lucide-react';

interface ForgetPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  userEmail: string;
  onSendResetLink: (email: string) => Promise<{ error: string | null }>;
}

export function ForgetPasswordModal({
  isOpen,
  onClose,
  userEmail,
  onSendResetLink
}: ForgetPasswordModalProps) {
  const { t } = useTranslation();
  const [email, setEmail] = useState(userEmail);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setIsLoading(true);

    try {
      const { error: sendError } = await onSendResetLink(email);
      
      if (sendError) {
        // 1.2.9: 显示详细的错误信息
        setError(sendError);
        setIsLoading(false);
        return;
      }

      // 1.2.9: 显示成功消息，包含本地环境提示
      setSuccess(true);
      setIsLoading(false);
      
      // Close modal after 5 seconds (增加时间以便用户看到提示)
      setTimeout(() => {
        onClose();
        setSuccess(false);
        setError(null);
      }, 5000);
    } catch (err: any) {
      // 1.2.9: 显示详细的异常信息
      const errorMessage = err.message || '发送重置链接失败';
      setError(errorMessage);
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black bg-opacity-50 z-50"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* 2.0.0: 添加暗色主题支持 - Modal 容器背景和阴影 */}
            <div className="bg-white dark:bg-gray-950 rounded-xl shadow-2xl dark:shadow-gray-900/50 w-full max-w-md">
              {/* Header */}
              {/* 2.0.0: 添加暗色主题支持 - Header 边框、标题和关闭按钮 */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {t('forgotPassword')}
                </h2>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors text-gray-400 hover:text-gray-600 dark:hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Confirmation Message */}
                {/* 2.0.0: 添加暗色主题支持 - 确认文字颜色 */}
                <p className="text-gray-700 dark:text-gray-300 mb-6">
                  {t('forgotPasswordConfirm')}
                </p>

                {/* Error Message */}
                {/* 2.0.0: 添加暗色主题支持 - 错误消息背景、边框和文字颜色 */}
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 flex items-center gap-2"
                  >
                    <AlertCircle className="w-5 h-5" />
                    <span>{error}</span>
                  </motion.div>
                )}

                {/* Success Message */}
                {/* 2.0.0: 添加暗色主题支持 - 成功消息背景、边框、文字颜色和开发提示文字 */}
                {success && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-4 p-3 bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-800 rounded-lg text-green-700 dark:text-green-300"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-5 h-5" />
                      <span>{t('resetLinkSent')}</span>
                    </div>
                    {import.meta.env.DEV && (
                      <p className="text-sm mt-2 text-green-600 dark:text-green-400">
                        {t('resetLinkSentLocal')}
                      </p>
                    )}
                  </motion.div>
                )}

                {/* Email Form */}
                {!success && (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Email Field */}
                    {/* 2.0.0: 添加暗色主题支持 - Label 和输入框样式 */}
                    <div>
                      <label htmlFor="reset-email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('userEmail')}
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          id="reset-email"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                          disabled={isLoading}
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                          placeholder="your@email.com"
                        />
                      </div>
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="w-full py-2.5 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-medium rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          {t('loading')}
                        </>
                      ) : (
                        <>
                          <Mail className="w-5 h-5" />
                          {t('sendResetLink')}
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

